import { useState, useCallback } from "react";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { Responsive, WidthProvider } from "react-grid-layout";
import {
  TOTAL_CELLS_IN_LAYOUT_BY_BREAKPOINT,
  BREAKPOINTS,
  getGridLayoutComponents,
  layoutReducer,
  getBreakpointKey,
  GridLayoutComponentsObj,
  useStorybookQuery,
} from "./gridLayoutPageUtils";
import useWindowSize from "utils/customHooks/useWindowSize";
import { useLocation } from "react-router";
import styled from "styled-components/macro";
import ErrorBoundary from "../../components/SimpleComponents/Errors/ErrorBoundary";
import { GRID_LAYOUT_COMPONENTS } from "./defaultGridLayoutComponents";
import { getQuery } from "pages/DashboardPages/dashboardPagesUtils";
import useContainerDimensions from "utils/useContainerDimensions";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { useMediaQuery } from "@mui/material";
import { BREAKPOINT_MOBILE, CHART_MAX_WIDTH } from "constants/constants";
import { GridLayoutTrendChart } from "./gridLayoutComponents/GridLayoutTrendChart";
import { GridLayoutPnlChart } from "./gridLayoutComponents/GridLayoutPnlChart";
import { GridLayoutHubletView } from "./gridLayoutComponents/GridLayoutHubletView";
import { GridLayoutTimeline } from "./gridLayoutComponents/GridLayoutTimeline";

// Grid Layout uses react-grid-layout: https://github.com/STRML/react-grid-layout
const ResponsiveGridLayout = WidthProvider(Responsive);

type GridLayoutProps = {
  width: number;
  height: number;
};
// tslint:disable-next-line
export default function GridLayout({
  height: layoutHeight,
  width: layoutWidth,
}: GridLayoutProps) {
  const isPrint = useMediaQuery("print");

  const size = useWindowSize();
  const [wrapperRef, dimensions] = useContainerDimensions();
  const initialWidth = isPrint
    ? CHART_MAX_WIDTH
    : layoutWidth || dimensions?.width || size?.innerWidth;

  const [breakpointKey, setBreakpointKey] = useState(
    getBreakpointKey(initialWidth)
  );
  const isStorybook = useIsStorybook();
  const storybookQuery = useStorybookQuery();
  const urlQuery = getQuery(useLocation());
  const layoutComponentsObj: GridLayoutComponentsObj = getGridLayoutComponents(
    isStorybook ? storybookQuery : urlQuery,
    breakpointKey
  );

  // an object of the layout array for each breakpoint e.g. {lg: layout1, md: layout2, ...}
  const layoutsByBreakpoint: ReactGridLayout.Layouts = Object.keys(
    BREAKPOINTS
  ).reduce((accumulatedLayouts, currentBreakpointKey) => {
    const layout: ReactGridLayout.Layout[] = Object.entries(
      layoutComponentsObj
    ).reduce(
      layoutReducer(
        TOTAL_CELLS_IN_LAYOUT_BY_BREAKPOINT[currentBreakpointKey].cols,
        TOTAL_CELLS_IN_LAYOUT_BY_BREAKPOINT[currentBreakpointKey].rows,
        currentBreakpointKey
      ),
      [] as ReactGridLayout.Layout[] // array of grid items = the full layout
    );
    return { ...accumulatedLayouts, [currentBreakpointKey]: layout };
  }, {} as ReactGridLayout.Layouts);

  const onBreakpointChange = newBreakpointKey => {
    if (isPrint) {
      return;
    }
    setBreakpointKey(newBreakpointKey);
  };

  const { rows: totalRowsInLayout, cols: totalColumnsInLayout } =
    TOTAL_CELLS_IN_LAYOUT_BY_BREAKPOINT[breakpointKey];

  const singleRowHeight = layoutHeight / totalRowsInLayout;
  const singleColumnWidth = layoutWidth / totalColumnsInLayout;

  const gridLayoutComponents = useGridLayoutComponents({
    layoutComponentsObj,
    layoutsForThisBreakpoint: layoutsByBreakpoint[breakpointKey],
    singleColumnWidth,
    singleRowHeight,
  });

  /**  An object of grid columns at each breakpoint for the GridLayout component e.g. {lg: 12, md: 10, sm: 6, xs: 4, xxs: 2}
   */
  const layoutsColsByBreakpoint: { [breakpoint: string]: number } = Object.keys(
    BREAKPOINTS
  ).reduce(
    (accumulator, currentBreakpoint) => {
      return {
        ...accumulator,
        [currentBreakpoint]:
          TOTAL_CELLS_IN_LAYOUT_BY_BREAKPOINT[currentBreakpoint].cols,
      };
    },
    {} as { [breakpoint: string]: number }
  );

  return (
    <GridLayoutStyles
      ref={wrapperRef}
      data-component-name={"Grid Layout Page"}
      style={{ position: "relative", height: "100%" }}
    >
      <ErrorBoundary id="GridLayout">
        <ResponsiveGridLayout
          measureBeforeMount={true} /* prevent animation */
          useCSSTransforms={
            false
          } /* prevent animation -- re-enable on mount if we implement drag-and-drop*/
          className="layout react-grid-layout"
          layouts={layoutsByBreakpoint}
          breakpoints={BREAKPOINTS}
          onBreakpointChange={onBreakpointChange}
          cols={layoutsColsByBreakpoint}
          rowHeight={singleRowHeight}
          width={layoutWidth}
          margin={[0, 0]}
        >
          {gridLayoutComponents.map(({ componentName, component }) => (
            <div className={"gridItem"} key={componentName}>
              {component}
            </div>
          ))}
        </ResponsiveGridLayout>
      </ErrorBoundary>
    </GridLayoutStyles>
  );
}

const GridLayoutStyles = styled.div`
  @media (max-width: ${BREAKPOINT_MOBILE + 1}px) {
    .react-grid-layout {
      height: calc(100vh - 64px) !important;
      overflow-y: scroll;
    }
  }
`;

function useGridLayoutComponents({
  layoutComponentsObj,
  layoutsForThisBreakpoint,
  singleColumnWidth,
  singleRowHeight,
}: {
  layoutComponentsObj: GridLayoutComponentsObj;
  layoutsForThisBreakpoint: ReactGridLayout.Layout[];
  singleColumnWidth: number;
  singleRowHeight: number;
}): { componentName: string; component: React.JSX.Element }[] {
  const getDataGridDimensionsForComponent = useCallback(
    (componentName: string) =>
      getDataGridDimensions(componentName, layoutsForThisBreakpoint, {
        singleColumnWidth,
        singleRowHeight,
      }),
    [layoutsForThisBreakpoint, singleColumnWidth, singleRowHeight]
  );
  const { dataGridWidth: trendChartWidth } = getDataGridDimensionsForComponent(
    GRID_LAYOUT_COMPONENTS.trendChart
  );
  const { dataGridWidth: timelinesWidth } = getDataGridDimensionsForComponent(
    GRID_LAYOUT_COMPONENTS.timeline
  );
  const { dataGridWidth: pnlChartWidth } = getDataGridDimensionsForComponent(
    GRID_LAYOUT_COMPONENTS.pnl
  );

  return Object.entries(layoutComponentsObj).reduce(
    (accumulator, [componentName, componentGridSpecs]) => {
      if (componentGridSpecs.shouldDisplay) {
        const component =
          componentName === GRID_LAYOUT_COMPONENTS.trendChart ? (
            <GridLayoutTrendChart
              {...{
                trendChartWidth,
              }}
            />
          ) : componentName === GRID_LAYOUT_COMPONENTS.hubletView ? (
            <GridLayoutHubletView />
          ) : componentName === GRID_LAYOUT_COMPONENTS.pnl ? (
            <GridLayoutPnlChart {...{ width: pnlChartWidth }} />
          ) : componentName === GRID_LAYOUT_COMPONENTS.timeline ? (
            <GridLayoutTimeline {...{ width: timelinesWidth }} />
          ) : (
            <div>bad name</div>
          );

        return [...accumulator, { componentName, component }];
      }
      return accumulator;
    },
    [] as {
      componentName: string;
      component: React.JSX.Element;
    }[]
  );
}

/** return height and width of the data-grid for a specified component block from the layout */
const getDataGridDimensions = (
  componentName: string,
  layoutsForThisBreakpoint: ReactGridLayout.Layout[],
  {
    singleColumnWidth,
    singleRowHeight,
  }: { singleColumnWidth: number; singleRowHeight: number }
) => {
  // data-grid is a component block from the layout, which has x, y, w, h (grid coordinates and dimensions)
  const dataGrid =
    layoutsForThisBreakpoint.find(dg => dg.i === componentName) || null;
  const dataGridWidth = dataGrid ? singleColumnWidth * dataGrid.w : 0; // dataGrid.w is a width measure in units of cell columns
  const dataGridHeight = dataGrid ? singleRowHeight * dataGrid.h : 0; // dataGrid.h is a height measure in units of cell rows
  return { dataGridWidth, dataGridHeight };
};
